import React, { ReactElement, useEffect } from 'react';
import { isSome, Option } from 'fp-ts/lib/Option';
import { FullScreen, FullScreenHandle, useFullScreenHandle } from 'react-full-screen';
import { useMediaQuery } from 'react-responsive';

import { ErrorData } from 'domain/types';
import { useReadDashboard } from 'adapter/in/dashboards/hooks/Dashboard/useReadDashboard';
import { useReadWidgets } from 'adapter/in/dashboards/hooks/Widgets/useReadWidgets';
import useAutorefreshDashboard from 'adapter/in/dashboards/hooks/Dashboard/useAutorefreshDashboard';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { DashboardResult } from 'adapter/in/dashboards/context/Dashboard';
import { dispatchWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import DashboardLinkPage from 'adapter/in/dashboards/pages/DashboardLink';
import Profile from 'domain/entity/profile';
import { getHierarchyIcon } from 'adapter/in/dashboards/utils/getHierarchyIcon';

type DashboardLinkContainerProps = {
    dashboardId: string;
};

export const useDashboardLinkContainer = (
    dashboardId: string
): {
    isLoading: boolean;
    dashboard: DashboardResult;
    error: Option<ErrorData>;
    handleFullscreen: FullScreenHandle;
    profile: Profile;
    isLandscape: boolean;
} => {
    const isLandscape = useMediaQuery({ query: '(orientation: landscape)' });
    const { profile } = useProfile();
    const { isLoading, data: dashboard, error, reset: readDashboardReset } = useReadDashboard(dashboardId);
    const { isLoading: readWidgetsIsLoading, data: readWidgetsResult, error: readWidgetsError, reset: readWidgetsReset } = useReadWidgets(
        dashboardId
    );
    const { setWidgets } = dispatchWidgetsView();

    useAutorefreshDashboard();

    useEffect(() => {
        if (isSome(dashboard) && isSome(readWidgetsResult) && readWidgetsResult.value.length > 0) {
            setWidgets(readWidgetsResult.value);
        }
    }, [dashboard, readWidgetsResult]);

    const onUnmountResetState = () => {
        readDashboardReset();
        readWidgetsReset();
    };

    useEffect(() => onUnmountResetState, []);

    return {
        isLoading: isLoading || readWidgetsIsLoading,
        dashboard,
        error: isSome(error) ? error : readWidgetsError,
        handleFullscreen: useFullScreenHandle(),
        profile,
        isLandscape,
    };
};

export const DashboardLinkContainer = (props: DashboardLinkContainerProps): ReactElement => {
    const { dashboardId: id } = props;
    const { dashboard, isLoading, error, handleFullscreen, profile, isLandscape } = useDashboardLinkContainer(id);
    const tags = [
        {
            label: profile.locationName,
            icon: getHierarchyIcon(profile.locationType),
        },
        ...profile.selectedLocationTags.map((tag) => ({
            label: tag,
            icon: 'tag',
        })),
    ];
    return (
        <FullScreen handle={handleFullscreen}>
            <DashboardLinkPage
                data={dashboard}
                tags={tags}
                onFullscreen={handleFullscreen.enter}
                fullscreenActive={handleFullscreen.active}
                isLandscape={isLandscape}
                isLoading={isLoading}
                error={error}
            />
        </FullScreen>
    );
};
